import { RootNodeContext } from '@/context/RootNodeContext';
import { findPathToTopic } from '@/helpers/node';
import { useInternalLink } from '@/hooks/useInternalLink';
import DOMPurify from 'dompurify';
import parse, {
  DOMNode,
  domToReact,
  Element,
  HTMLReactParserOptions,
} from 'html-react-parser';
import * as React from 'react';
import { getProcedureCarouselData } from '../helpers/topic';
import Anchor from './topic/Anchor';
import Div from './topic/Div';
import ExternalAnchor from './topic/ExternalAnchor';
import Img from './topic/Img';
import List from './topic/List';
import Paragraph from './topic/Paragraph';
import Procedure from './topic/Procedure';
import Span from './topic/Span';
import Table from './topic/Table';
import Video from './topic/Video';

type StringToComponentProps = {
  data: string;
};

const StringToComponent: React.FC<StringToComponentProps> = ({ data }) => {
  const [ReactComponent, setReactComponent] = React.useState<
    string | React.ReactNode | React.ReactNode[]
  >('');
  const rootNode = React.useContext(RootNodeContext);
  const { internalLink } = useInternalLink();

  React.useEffect(() => {
    if (data && rootNode) {
      const options: HTMLReactParserOptions = {
        replace: (domNode) => {
          if (!(domNode instanceof Element) || !domNode.attribs) return;

          const node = domNode as unknown as Elvita.Topic;

          switch (domNode.name) {
            case 'a': {
              if (domNode.attribs.class === 'xref') {
                const topicId = domNode.attribs.href;
                if (/^[DS]\d+.*_[a-z]{2}-[A-Z]{2}$/.test(topicId)) {
                  const path = findPathToTopic(rootNode, topicId);
                  const realParent =
                    path.length > 1 ? path[path.length - 2] : undefined;
                  let url = `/browse/${topicId}`;
                  if (realParent) {
                    url = `/${realParent.topicId}/topics/${topicId}`;
                  }
                  return (
                    <Anchor href={internalLink(url)}>
                      {domToReact(domNode.children as DOMNode[], options)}
                    </Anchor>
                  );
                }
                return (
                  <Anchor href={domNode.attribs.href}>
                    {domToReact(domNode.children as DOMNode[], options)}
                  </Anchor>
                );
              } else {
                const href = domNode.attribs.href?.startsWith('www.')
                  ? 'http://' + domNode.attribs.href
                  : domNode.attribs.href;
                return (
                  <ExternalAnchor href={href}>
                    {domToReact(domNode.children as DOMNode[], options)}
                  </ExternalAnchor>
                );
              }
            }
            case 'div':
              return (
                <Div node={node}>
                  {domToReact(domNode.children as DOMNode[], options)}
                </Div>
              );
            case 'img':
              return (
                <Img imageId={domNode.attribs['src']} node={node}>
                  {domToReact(domNode.children as DOMNode[], options)}
                </Img>
              );

            case 'table': {
              // Filter out empty colgroup elements from table children
              const filteredChildren = domNode.children.filter(
                (child): child is Element => {
                  if (
                    child instanceof Element &&
                    child.name === 'colgroup' &&
                    child.children.length === 0
                  ) {
                    return false;
                  }
                  return true;
                },
              );

              // Create a modified node with filtered children
              const modifiedNode = {
                ...node,
                children: filteredChildren as unknown as Elvita.Topic[],
              };

              return (
                <Table node={modifiedNode}>
                  {domToReact(filteredChildren as DOMNode[], options)}
                </Table>
              );
            }

            case 'colgroup': {
              // Filter out empty colgroups that don't have children or col elements
              if (!domNode.children || domNode.children.length === 0) {
                return null;
              }
              // Keep colgroups that have col elements
              return domNode;
            }

            case 'p':
              return (
                <Paragraph lang={domNode.attribs.lang}>
                  {domToReact(domNode.children as DOMNode[], options)}
                </Paragraph>
              );

            case 'ul':
            case 'ol': {
              const procedureData = getProcedureCarouselData(node, data);

              if (procedureData) {
                return (
                  <Procedure items={procedureData.data} id={procedureData.id} />
                );
              }

              return (
                <List node={node}>
                  {domToReact(domNode.children as DOMNode[], options)}
                </List>
              );
            }

            case 'span':
              return (
                <Span className={domNode.attribs.class} lang={domNode.attribs.lang}>
                  {domToReact(domNode.children as DOMNode[], options)}
                </Span>
              );

            case 'video':
              return <Video node={node} />;

            default:
              return domNode;
          }
        },
      };
      setReactComponent(parse(DOMPurify.sanitize(data), options));
    }
  }, [data, rootNode, internalLink]);

  if (!data) {
    return null;
  }

  return <>{ReactComponent}</>;
};

export default StringToComponent;
